/**
 * @description Untitled UI-inspired Recharts primitives for Reporting.
 * @dependencies recharts, formatCurrency
 * @notes Copy/paste styling from Untitled UI line & bar charts (Recharts + Tailwind).
 * No untitledui package — Lynx already has recharts. Series color is brand-primary.
 */

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Sector,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { formatCurrency } from '../../utils/invoiceHelpers';

export const CHART = Object.freeze({
  brand: 'hsl(var(--brand-primary))',
  brandHover: 'hsl(var(--brand-primary-hover))',
  new: '#17B26A',
  churn: '#F04438',
  service: '#0BA5EC',
  today: '#F79009',
  grid: '#F2F4F7',
  tick: '#667085',
  label: '#181D27'
});

const tick = { fontSize: 12, fill: CHART.tick };

/**
 * Line/bar chart clicks nest the row on activePayload[0].payload.
 * Scatter clicks nest it on payload.
 *
 * @param {object} event
 * @returns {object|null|undefined}
 */
export function chartEventRow(event) {
  if (!event || typeof event !== 'object') return event;
  if (event.activePayload?.[0]?.payload) return event.activePayload[0].payload;
  if (event.payload) return event.payload;
  return event;
}

export function ChartTooltip({ active, payload, label, valueFormatter, valueOnly, hoverLine }) {
  if (!active || !payload?.length) return null;
  if (valueOnly) {
    const entry = payload[0];
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-lg">
        <span className="text-sm font-semibold text-gray-900">
          {valueFormatter ? valueFormatter(entry.value, entry) : entry.value}
        </span>
      </div>
    );
  }
  const line = hoverLine ? hoverLine(payload[0]?.payload, { payload, label }) : null;
  if (line) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-lg">
        <div className="text-sm text-gray-900">{line}</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-lg">
      {label ? <div className="mb-1 text-xs font-medium text-gray-500">{label}</div> : null}
      <div className="space-y-1">
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-2 text-sm text-gray-900">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-500">{entry.name}</span>
            <span className="ml-auto font-semibold">
              {valueFormatter ? valueFormatter(entry.value, entry) : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {{ title: string, subtitle?: string, action?: import('react').ReactNode, caption?: string|null, captionTestId?: string, className?: string, fixedPlot?: boolean, children: import('react').ReactNode }} props
 */
export function ChartCard({
  title,
  subtitle,
  action,
  caption,
  captionTestId = 'chart-card-caption',
  className,
  fixedPlot = false,
  children
}) {
  return (
    <section className={`flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm ${className || ''}`}>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-md font-semibold text-gray-900">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      <div className={fixedPlot ? 'h-72 shrink-0 overflow-hidden' : 'h-72 min-h-[18rem] flex-1'}>{children}</div>
      {caption ? (
        <p
          className="mt-3 min-h-10 text-center text-sm leading-5 text-gray-600"
          data-testid={captionTestId}
        >
          {caption}
        </p>
      ) : null}
    </section>
  );
}

export function ChartEmpty({ text }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-gray-500">
      {text}
    </div>
  );
}

export function ChartLegend({ items }) {
  return (
    <div className="flex flex-wrap gap-3">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Untitled line-chart 01: area fill + line, no axis lines, horizontal grid only.
 */
export function ReportingLineChart({
  data,
  dataKey = 'dollars',
  name = 'Amount',
  color = CHART.brand,
  valueFormatter = formatCurrency,
  onPointClick,
  xAxisLabel,
  yAxisLabel,
  domain,
  tooltipValueOnly = false
}) {
  const gradientId = `reporting-fill-${dataKey}`;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{ top: 8, right: 8, left: 8, bottom: xAxisLabel ? 16 : 0 }}
        onClick={(state) => {
          const row = chartEventRow(state);
          if (row && onPointClick) onPointClick(row);
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.24} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={CHART.grid} />
        <XAxis dataKey="label" axisLine={false} tickLine={false} tick={tick} dy={8}>
          {xAxisLabel ? (
            <Label value={xAxisLabel} position="insideBottom" offset={-12} fill={CHART.tick} fontSize={12} />
          ) : null}
        </XAxis>
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={tick}
          width={yAxisLabel ? 64 : 56}
          domain={domain}
          tickFormatter={(value) => valueFormatter(value)}
        >
          {yAxisLabel ? (
            <Label value={yAxisLabel} angle={-90} position="insideLeft" fill={CHART.tick} fontSize={12} />
          ) : null}
        </YAxis>
        <Tooltip
          cursor={{ stroke: CHART.grid, strokeWidth: 1 }}
          content={<ChartTooltip valueFormatter={valueFormatter} valueOnly={tooltipValueOnly} />}
        />
        <Area
          type="monotone"
          dataKey={dataKey}
          name={name}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={{ r: 3, fill: '#fff', stroke: color, strokeWidth: 2 }}
          activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: color }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/**
 * Untitled bar-chart 01: rounded columns, no axis lines.
 */
/**
 * @param {object} props
 * @param {boolean} [props.hideCategoryTicks] Hide per-bar names. Keep one axis label.
 * @param {(row: object, meta?: object) => string|null} [props.hoverLine]
 */
export function ReportingBarChart({
  data,
  bars,
  valueFormatter = formatCurrency,
  layout = 'column',
  onBarClick,
  xAxisLabel,
  yAxisLabel,
  hideCategoryTicks = false,
  hoverLine
}) {
  const isBar = layout === 'bar';
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={isBar ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 8, left: isBar ? 8 : 8, bottom: xAxisLabel ? 16 : 0 }}
        barCategoryGap="28%"
        onClick={(state) => {
          const row = chartEventRow(state);
          if (row && onBarClick) onBarClick(row);
        }}
      >
        <CartesianGrid vertical={false} stroke={CHART.grid} />
        {isBar ? (
          <>
            <XAxis type="number" axisLine={false} tickLine={false} tick={tick} tickFormatter={valueFormatter}>
              {xAxisLabel ? (
                <Label value={xAxisLabel} position="insideBottom" offset={-12} fill={CHART.tick} fontSize={12} />
              ) : null}
            </XAxis>
            <YAxis
              type="category"
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={hideCategoryTicks ? false : tick}
              width={hideCategoryTicks ? 16 : 120}
            >
              {yAxisLabel ? (
                <Label value={yAxisLabel} angle={-90} position="insideLeft" fill={CHART.tick} fontSize={12} />
              ) : null}
            </YAxis>
          </>
        ) : (
          <>
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={hideCategoryTicks ? false : tick}
              dy={hideCategoryTicks ? 0 : 8}
            >
              {xAxisLabel ? (
                <Label value={xAxisLabel} position="insideBottom" offset={-12} fill={CHART.tick} fontSize={12} />
              ) : null}
            </XAxis>
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={tick}
              width={yAxisLabel ? 64 : 56}
              tickFormatter={valueFormatter}
            >
              {yAxisLabel ? (
                <Label value={yAxisLabel} angle={-90} position="insideLeft" fill={CHART.tick} fontSize={12} />
              ) : null}
            </YAxis>
          </>
        )}
        <Tooltip
          cursor={{ fill: '#F9FAFB' }}
          content={<ChartTooltip valueFormatter={valueFormatter} hoverLine={hoverLine} />}
        />
        {bars.map((bar) => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            name={bar.name}
            fill={bar.color}
            maxBarSize={40}
            radius={isBar ? [0, 6, 6, 0] : [6, 6, 0, 0]}
          >
            {data.map((row, index) => (
              <Cell key={`${bar.dataKey}-${index}`} fill={row.color || bar.color} />
            ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieActiveShape(props) {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value
  } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <text x={cx} y={cy - 8} textAnchor="middle" fill={CHART.label} fontSize={13} fontWeight={600}>
        {payload?.label}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill={CHART.tick} fontSize={12}>
        {value} · {Math.round((percent || 0) * 100)}%
      </text>
    </g>
  );
}

/**
 * Response pie: under half / on time / late. Hover expands the active slice.
 * Scope 1 BI copy is a ChartCard caption, not a donut-hole overlay.
 *
 * @param {object} props
 */
export function ReportingPieChart({
  data,
  dataKey = 'count',
  nameKey = 'label',
  valueFormatter = (value, entry) => {
    const pct = entry?.percent == null ? '' : ` · ${Math.round(entry.percent * 100)}%`;
    return `${value}${pct}`;
  }
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          innerRadius={52}
          outerRadius={80}
          paddingAngle={2}
          activeShape={PieActiveShape}
        >
          {(data || []).map((row, index) => (
            <Cell key={`${row[nameKey] || index}`} fill={row.color} />
          ))}
        </Pie>
        <Tooltip
          content={<ChartTooltip valueFormatter={valueFormatter} />}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

/**
 * Profit vs pace scatter. Faster (smaller X) stays to the left.
 *
 * @param {object} props
 */
export function ReportingScatterChart({
  data,
  xKey = 'x',
  yKey = 'y',
  nameKey = 'name',
  xMedian,
  yMedian,
  xAxisLabel,
  xFormatter = (value) => value,
  yFormatter = (value) => value,
  onPointClick
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={{ top: 8, right: 8, left: 8, bottom: 36 }}>
        <CartesianGrid stroke={CHART.grid} />
        <XAxis
          type="number"
          dataKey={xKey}
          name="Response"
          axisLine={false}
          tickLine={false}
          tick={tick}
          tickFormatter={xFormatter}
          height={36}
          reversed={false}
        >
          <Label
            value={xAxisLabel || 'Median response (enroute to on scene)'}
            position="insideBottom"
            offset={0}
            fill={CHART.tick}
            fontSize={12}
          />
        </XAxis>
        <YAxis
          type="number"
          dataKey={yKey}
          name="Invoice"
          axisLine={false}
          tickLine={false}
          tick={tick}
          width={64}
          tickFormatter={yFormatter}
        >
          <Label value="Avg invoice" angle={-90} position="insideLeft" fill={CHART.tick} fontSize={12} />
        </YAxis>
        {xMedian != null ? <ReferenceLine x={xMedian} stroke={CHART.tick} strokeDasharray="4 4" /> : null}
        {yMedian != null ? <ReferenceLine y={yMedian} stroke={CHART.tick} strokeDasharray="4 4" /> : null}
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0].payload;
            return (
              <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-lg">
                <div className="mb-1 text-xs font-medium text-gray-500">{row[nameKey]}</div>
                <div className="text-sm text-gray-900">{xFormatter(row[xKey])}</div>
                <div className="text-sm text-gray-900">{yFormatter(row[yKey])}</div>
              </div>
            );
          }}
        />
        <Scatter
          data={data}
          fill={CHART.brand}
          onClick={(event) => {
            const row = chartEventRow(event);
            if (row && onPointClick) onPointClick(row);
          }}
        >
          {(data || []).map((row, index) => (
            <Cell
              key={`${row[nameKey] || index}`}
              fill={row.color || CHART.brand}
              cursor={onPointClick ? 'pointer' : 'default'}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}
